import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  AssignmentTurnedIn as ValidatorIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
  Edit as EditIcon,
  Link as LinkIcon,
  Tune as TuneIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import { agentApi } from '../../../services/api';
import type {
  AgentValidator,
  AgentValidatorAction,
  AgentValidatorAssignment,
  AgentValidatorAssignmentRequest,
  AgentValidatorCreate,
  AgentValidatorHook,
  AgentValidatorMode,
  AgentValidatorSeverity,
} from '../../../types';
import { AgentAutocomplete } from '../../common/AsyncEntitySelect';
import { SectionCard } from './primitives';

interface ValidatorsTabProps {
  agentId: number;
}

const HOOK_OPTIONS: { value: AgentValidatorHook; label: string; description: string }[] = [
  {
    value: 'before_model_call',
    label: 'Before model call',
    description: 'Validate the prompt/messages before the LLM is called.',
  },
  {
    value: 'after_model_call',
    label: 'After model call',
    description: 'Validate the raw model output before tool execution or response handling.',
  },
  {
    value: 'before_tool_call',
    label: 'Before tool call',
    description: 'Validate the selected tool and arguments before the tool runs.',
  },
  {
    value: 'after_tool_call',
    label: 'After tool call',
    description: 'Validate the tool result before it is added back to the agent context.',
  },
  {
    value: 'before_final_response',
    label: 'Before final response',
    description: 'Validate the answer before it is returned to the user.',
  },
  {
    value: 'after_final_response',
    label: 'After final response',
    description: 'Audit the final answer after it has been produced.',
  },
];

const ACTION_OPTIONS: {
  value: AgentValidatorAction | '';
  label: string;
  description: string;
}[] = [
  {
    value: '',
    label: 'Use validator default',
    description: 'Keep the action configured on the validator.',
  },
  { value: 'allow', label: 'Allow', description: 'Continue execution.' },
  { value: 'block', label: 'Block', description: 'Stop this step and return the validation failure.' },
  {
    value: 'require_confirmation',
    label: 'Require confirmation',
    description: 'Pause until the user confirms the step.',
  },
  {
    value: 'retry_with_feedback',
    label: 'Retry with feedback',
    description: 'Ask the agent to retry with validator feedback.',
  },
  {
    value: 'rewrite_args',
    label: 'Rewrite tool arguments',
    description: 'Use validator-provided replacement tool arguments.',
  },
  {
    value: 'redact_result',
    label: 'Redact tool result',
    description: 'Replace the tool result with validator-provided redacted content.',
  },
  {
    value: 'skip_memory_write',
    label: 'Skip memory write',
    description: 'Allow execution but do not persist this tool result to memory.',
  },
  { value: 'notify_user', label: 'Notify user', description: 'Record a notice while allowing execution.' },
  { value: 'mark_for_review', label: 'Mark for review', description: 'Record the event for later review.' },
  {
    value: 'stop_execution',
    label: 'Stop execution',
    description: 'Terminate the current agent execution.',
  },
];

const MODE_OPTIONS: {
  value: AgentValidatorMode;
  label: string;
  description: string;
}[] = [
  {
    value: 'agent',
    label: 'Validator agent',
    description: 'Run another agent as the judge. The prompt below tells it what to verify.',
  },
  {
    value: 'deterministic',
    label: 'Rule checks',
    description: 'Run configured checks only. This does not call an LLM.',
  },
  {
    value: 'hybrid',
    label: 'Rules, then agent',
    description: 'Run checks first. If they pass, run the selected validator agent.',
  },
];

const SEVERITY_OPTIONS: {
  value: AgentValidatorSeverity;
  label: string;
  description: string;
}[] = [
  { value: 'low', label: 'Low', description: 'Routine audit event.' },
  { value: 'medium', label: 'Medium', description: 'Normal validation event.' },
  { value: 'high', label: 'High', description: 'Important event to review.' },
  { value: 'critical', label: 'Critical', description: 'Critical event to prioritize in audit views.' },
];

const TARGET_TYPE_OPTIONS = [
  {
    value: '',
    label: 'Any target',
    description: 'Run for every target that reaches this hook.',
  },
  {
    value: 'tool',
    label: 'Tool calls',
    description: 'Only tool calls. Target names are tool names.',
  },
  {
    value: 'model',
    label: 'Model calls',
    description: 'Only model calls. Target names are model identifiers.',
  },
  {
    value: 'final_response',
    label: 'Final response',
    description: 'Only final assistant responses.',
  },
];

const CHECK_OPTIONS: { value: string; label: string; description: string }[] = [
  {
    value: 'always_pass',
    label: 'Always pass',
    description: 'Useful for agent-only validation or audit-only policies.',
  },
  {
    value: 'always_fail',
    label: 'Always fail',
    description: 'Useful for testing validator actions.',
  },
  {
    value: 'tool_allowed',
    label: 'Tool is allowed',
    description: 'Fail if the requested tool is not allowed for this agent.',
  },
  {
    value: 'allowed_tools',
    label: 'Only allowed tools',
    description: 'Fail unless the tool is listed in config.allowed_tools.',
  },
  {
    value: 'blocked_tools',
    label: 'Block listed tools',
    description: 'Fail when the tool is listed in config.blocked_tools.',
  },
  {
    value: 'max_tool_danger_level',
    label: 'Max tool danger level',
    description: 'Fail when a tool is riskier than config.max_danger_level.',
  },
  {
    value: 'required_args',
    label: 'Required tool arguments',
    description: 'Fail if required tool arguments are missing.',
  },
  {
    value: 'result_success',
    label: 'Tool result succeeded',
    description: 'Fail if the tool result reports an error.',
  },
  {
    value: 'result_size_limit',
    label: 'Tool result size limit',
    description: 'Fail if the tool result exceeds config.max_chars.',
  },
  {
    value: 'no_empty_response',
    label: 'No empty response',
    description: 'Fail if the response is empty.',
  },
  {
    value: 'min_response_length',
    label: 'Minimum response length',
    description: 'Fail if the response is shorter than config.min_chars.',
  },
  {
    value: 'required_terms',
    label: 'Required terms',
    description: 'Fail if response text is missing config.required_terms.',
  },
  {
    value: 'blocked_terms',
    label: 'Blocked terms',
    description: 'Fail if response text contains config.blocked_terms.',
  },
  {
    value: 'json_required_keys',
    label: 'Required JSON keys',
    description: 'Fail if JSON output is missing config.required_keys.',
  },
  {
    value: 'message_size_limit',
    label: 'Message size limit',
    description: 'Fail if model input exceeds config.max_message_chars.',
  },
  {
    value: 'prompt_blocked_terms',
    label: 'Prompt blocked terms',
    description: 'Fail if model input contains config.blocked_terms.',
  },
  {
    value: 'blocked_tool_calls',
    label: 'Blocked tool calls',
    description: 'Fail if the model requested config.blocked_tool_calls.',
  },
];

const csvToList = (value: string): string[] =>
  value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const listToCsv = (value?: string[]): string => (value || []).join(', ');

const parseJson = (value: string, fallback: Record<string, any> = {}) => {
  if (!value.trim()) return fallback;
  return JSON.parse(value);
};

const optionLabel = (
  options: { value: string; label: string }[],
  value: string
): string => options.find((option) => option.value === value)?.label || value;

const optionDescription = (
  options: { value: string; description: string }[],
  value: string
): string => options.find((option) => option.value === value)?.description || '';

const DEFAULT_VALIDATOR_PROMPT =
  'Review the provided runtime context for this validation hook. Decide whether it satisfies the validator goal, then return a concise pass or fail verdict with the key findings and actionable feedback. If the result should be blocked or retried, explain the exact issue and what needs to change.';

const validatorDefaults = (): AgentValidatorCreate => ({
  name: '',
  description: '',
  validator_agent_id: null,
  validation_mode: 'agent',
  hook: 'before_tool_call',
  target_type: '',
  target_names: [],
  prompt: DEFAULT_VALIDATOR_PROMPT,
  checks: [],
  config: {},
  on_pass: 'allow',
  on_fail: 'block',
  severity: 'medium',
  max_retries: 0,
  timeout_seconds: 60,
  is_active: true,
});

const assignmentDefaults = (): AgentValidatorAssignmentRequest => ({
  validator: 0,
  is_enabled: true,
  order: 100,
  hook_override: '',
  target_type_override: '',
  target_names_override: [],
  prompt_override: '',
  config_override: {},
  on_pass_override: '',
  on_fail_override: '',
  max_retries_override: null,
});

export const ValidatorsTab: React.FC<ValidatorsTabProps> = ({ agentId }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [validatorOpen, setValidatorOpen] = useState(false);
  const [assignmentOpen, setAssignmentOpen] = useState(false);
  const [editingValidator, setEditingValidator] = useState<AgentValidator | null>(null);
  const [editingAssignment, setEditingAssignment] =
    useState<AgentValidatorAssignment | null>(null);
  const [validatorForm, setValidatorForm] =
    useState<AgentValidatorCreate>(validatorDefaults());
  const [assignmentForm, setAssignmentForm] =
    useState<AgentValidatorAssignmentRequest>(assignmentDefaults());
  const [validatorTargets, setValidatorTargets] = useState('');
  const [validatorChecks, setValidatorChecks] = useState('');
  const [validatorConfig, setValidatorConfig] = useState('{}');
  const [assignmentTargets, setAssignmentTargets] = useState('');
  const [assignmentConfig, setAssignmentConfig] = useState('{}');

  const validatorsQuery = useQuery({
    queryKey: ['agent-validators', agentId],
    queryFn: () => agentApi.getValidators(agentId),
    enabled: !!agentId,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['agent-validators', agentId] });
    queryClient.invalidateQueries({ queryKey: ['agent', agentId] });
    queryClient.invalidateQueries({ queryKey: ['agent-validators-list'] });
  };

  const createValidatorMutation = useMutation({
    mutationFn: (payload: AgentValidatorCreate) => agentApi.createValidator(payload),
    onSuccess: () => {
      toast.success('Validator saved');
      refresh();
      closeValidatorDialog();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || 'Failed to save validator');
    },
  });

  const updateValidatorMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: AgentValidatorCreate }) =>
      agentApi.updateValidator(id, payload),
    onSuccess: () => {
      toast.success('Validator saved');
      refresh();
      closeValidatorDialog();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || 'Failed to save validator');
    },
  });

  const assignMutation = useMutation({
    mutationFn: (payload: AgentValidatorAssignmentRequest) =>
      agentApi.assignValidator(agentId, payload),
    onSuccess: () => {
      toast.success('Validator assigned');
      refresh();
      closeAssignmentDialog();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || 'Failed to assign validator');
    },
  });

  const updateAssignmentMutation = useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number;
      payload: Partial<AgentValidatorAssignmentRequest>;
    }) => agentApi.updateValidatorAssignment(agentId, id, payload),
    onSuccess: () => {
      refresh();
      closeAssignmentDialog();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || 'Failed to update validator');
    },
  });

  const unassignMutation = useMutation({
    mutationFn: (assignmentId: number) =>
      agentApi.unassignValidator(agentId, { assignmentId }),
    onSuccess: () => {
      toast.success('Validator removed');
      refresh();
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.detail || 'Failed to remove validator');
    },
  });

  const assigned = validatorsQuery.data?.assigned || [];
  const available = validatorsQuery.data?.available || [];
  const assignmentValidatorOptions = useMemo(() => {
    if (!editingAssignment?.validator_details) return available;
    const alreadyIncluded = available.some(
      (validator) => validator.id === editingAssignment.validator_details?.id
    );
    if (alreadyIncluded) return available;
    return [editingAssignment.validator_details, ...available];
  }, [available, editingAssignment]);
  const currentMode = validatorForm.validation_mode || 'agent';
  const usesAgentJudge = currentMode === 'agent' || currentMode === 'hybrid';
  const usesRuleChecks = currentMode === 'deterministic' || currentMode === 'hybrid';
  const currentModeDescription = optionDescription(MODE_OPTIONS, currentMode);
  const currentHookDescription = optionDescription(HOOK_OPTIONS, validatorForm.hook);
  const currentSeverityDescription = optionDescription(
    SEVERITY_OPTIONS,
    validatorForm.severity || 'medium'
  );
  const currentTargetTypeDescription = optionDescription(
    TARGET_TYPE_OPTIONS,
    validatorForm.target_type || ''
  );

  const stats = useMemo(() => {
    const enabled = assigned.filter((item) => item.is_enabled).length;
    const blocking = assigned.filter(
      (item) => item.effective_on_fail === 'block' || item.effective_on_fail === 'stop_execution'
    ).length;
    return { assigned: assigned.length, enabled, blocking, available: available.length };
  }, [assigned, available]);

  const openNewValidator = () => {
    setEditingValidator(null);
    setValidatorForm(validatorDefaults());
    setValidatorTargets('');
    setValidatorChecks('');
    setValidatorConfig('{}');
    setValidatorOpen(true);
  };

  const openEditValidator = (validator: AgentValidator) => {
    setEditingValidator(validator);
    setValidatorForm({
      name: validator.name,
      description: validator.description,
      validator_agent_id: validator.validator_agent,
      validation_mode: validator.validation_mode,
      hook: validator.hook,
      target_type: validator.target_type,
      target_names: validator.target_names,
      prompt: validator.prompt,
      checks: validator.checks,
      config: validator.config,
      on_pass: validator.on_pass,
      on_fail: validator.on_fail,
      severity: validator.severity,
      max_retries: validator.max_retries,
      timeout_seconds: validator.timeout_seconds,
      is_active: validator.is_active,
    });
    setValidatorTargets(listToCsv(validator.target_names));
    setValidatorChecks(listToCsv(validator.checks));
    setValidatorConfig(JSON.stringify(validator.config || {}, null, 2));
    setValidatorOpen(true);
  };

  const closeValidatorDialog = () => {
    setValidatorOpen(false);
    setEditingValidator(null);
  };

  const openNewAssignment = (validatorId?: number) => {
    setEditingAssignment(null);
    setAssignmentForm({
      ...assignmentDefaults(),
      validator: validatorId ?? available[0]?.id ?? 0,
    });
    setAssignmentTargets('');
    setAssignmentConfig('{}');
    setAssignmentOpen(true);
  };

  const openEditAssignment = (assignment: AgentValidatorAssignment) => {
    setEditingAssignment(assignment);
    setAssignmentForm({
      validator: assignment.validator,
      is_enabled: assignment.is_enabled,
      order: assignment.order,
      hook_override: assignment.hook_override,
      target_type_override: assignment.target_type_override,
      target_names_override: assignment.target_names_override,
      prompt_override: assignment.prompt_override,
      config_override: assignment.config_override,
      on_pass_override: assignment.on_pass_override,
      on_fail_override: assignment.on_fail_override,
      max_retries_override: assignment.max_retries_override,
    });
    setAssignmentTargets(listToCsv(assignment.target_names_override));
    setAssignmentConfig(JSON.stringify(assignment.config_override || {}, null, 2));
    setAssignmentOpen(true);
  };

  const closeAssignmentDialog = () => {
    setAssignmentOpen(false);
    setEditingAssignment(null);
  };

  const saveValidator = () => {
    let parsedConfig: Record<string, any>;
    try {
      parsedConfig = parseJson(validatorConfig);
    } catch {
      toast.error('Config must be valid JSON');
      return;
    }
    const selectedChecks = csvToList(validatorChecks);
    const nextChecks = usesRuleChecks ? selectedChecks : [];
    if (usesAgentJudge && !validatorForm.validator_agent_id) {
      toast.error('Select the validator agent that should judge this validation.');
      return;
    }
    if (usesRuleChecks && nextChecks.length === 0) {
      toast.error('Select at least one rule check for this validation method.');
      return;
    }
    const payload: AgentValidatorCreate = {
      ...validatorForm,
      validator_agent_id: usesAgentJudge ? validatorForm.validator_agent_id : null,
      target_names: csvToList(validatorTargets),
      checks: nextChecks,
      config: parsedConfig,
    };
    if (editingValidator) {
      updateValidatorMutation.mutate({ id: editingValidator.id, payload });
    } else {
      createValidatorMutation.mutate(payload);
    }
  };

  const saveAssignment = () => {
    let parsedConfig: Record<string, any>;
    try {
      parsedConfig = parseJson(assignmentConfig);
    } catch {
      toast.error('Override config must be valid JSON');
      return;
    }
    const payload: AgentValidatorAssignmentRequest = {
      ...assignmentForm,
      target_names_override: csvToList(assignmentTargets),
      config_override: parsedConfig,
    };
    if (editingAssignment) {
      const updatePayload = { ...payload };
      delete (updatePayload as Partial<AgentValidatorAssignmentRequest>).validator;
      updateAssignmentMutation.mutate({
        id: editingAssignment.id,
        payload: updatePayload,
      });
    } else {
      if (!payload.validator) {
        toast.error('Select a validator');
        return;
      }
      assignMutation.mutate(payload);
    }
  };

  if (validatorsQuery.error) {
    return <Alert severity="error">Failed to load validators.</Alert>;
  }

  return (
    <Stack spacing={{ xs: 2, sm: 2.5 }}>
      <SectionCard
        title="Validators"
        icon={<ValidatorIcon fontSize="small" />}
        actions={
          <Stack direction="row" spacing={1}>
            <Button size="small" startIcon={<AddIcon />} onClick={openNewValidator}>
              New
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<LinkIcon />}
              onClick={() => openNewAssignment()}
              disabled={available.length === 0}
            >
              Assign
            </Button>
          </Stack>
        }
        description={
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip size="small" variant="outlined" label={`${stats.assigned} assigned`} />
            <Chip size="small" variant="outlined" label={`${stats.available} available`} />
            <Chip size="small" color="success" variant="outlined" label={`${stats.enabled} enabled`} />
            <Chip size="small" color="warning" variant="outlined" label={`${stats.blocking} blocking`} />
          </Stack>
        }
      >
        {validatorsQuery.isLoading ? (
          <Typography variant="body2" color="text.secondary">
            Loading validators...
          </Typography>
        ) : (
          <Stack spacing={2}>
            <Stack spacing={1.25}>
              <Typography variant="subtitle2" fontWeight={700}>
                Assigned to this agent
              </Typography>
              {assigned.length === 0 ? (
                <Alert severity="info">No validators assigned.</Alert>
              ) : (
                assigned.map((assignment) => {
                  const validator = assignment.validator_details;
                  return (
                    <Box
                      key={assignment.id}
                      sx={{
                        border: 1,
                        borderColor: assignment.is_enabled
                          ? alpha(theme.palette.primary.main, 0.4)
                          : 'divider',
                        borderRadius: 2,
                        p: 1.5,
                        bgcolor: assignment.is_enabled
                          ? alpha(theme.palette.primary.main, 0.04)
                          : 'background.paper',
                      }}
                    >
                      <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={1.5}
                        justifyContent="space-between"
                      >
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                            <Typography variant="subtitle2" fontWeight={700}>
                              {validator?.name || `Validator ${assignment.validator}`}
                            </Typography>
                            <Chip size="small" label={assignment.effective_hook.replace(/_/g, ' ')} />
                            <Chip
                              size="small"
                              color={assignment.effective_on_fail === 'allow' ? 'default' : 'warning'}
                              variant="outlined"
                              label={assignment.effective_on_fail.replace(/_/g, ' ')}
                            />
                            {validator?.validation_mode ? (
                              <Chip size="small" variant="outlined" label={validator.validation_mode} />
                            ) : null}
                          </Stack>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5, wordBreak: 'break-word' }}
                          >
                            {validator?.description || validator?.prompt || 'No description'}
                          </Typography>
                          {assignment.effective_target_names.length > 0 ? (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                              {assignment.effective_target_names.map((target) => (
                                <Chip key={target} size="small" variant="outlined" label={target} />
                              ))}
                            </Stack>
                          ) : null}
                        </Box>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Tooltip title={assignment.is_enabled ? 'Disable' : 'Enable'}>
                            <Switch
                              size="small"
                              color="success"
                              checked={assignment.is_enabled}
                              onChange={(e) =>
                                updateAssignmentMutation.mutate({
                                  id: assignment.id,
                                  payload: { is_enabled: e.target.checked },
                                })
                              }
                            />
                          </Tooltip>
                          <Tooltip title="Assignment">
                            <IconButton size="small" onClick={() => openEditAssignment(assignment)}>
                              <TuneIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {validator ? (
                            <Tooltip title="Validator">
                              <IconButton size="small" onClick={() => openEditValidator(validator)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                          <Tooltip title="Remove">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => unassignMutation.mutate(assignment.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    </Box>
                  );
                })
              )}
            </Stack>

            <Divider />

            <Stack spacing={1.25}>
              <Typography variant="subtitle2" fontWeight={700}>
                Available validators
              </Typography>
              {available.length === 0 ? (
                <Alert severity="info">No available validators.</Alert>
              ) : (
                available.map((validator) => (
                  <Box
                    key={validator.id}
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 2,
                      p: 1.5,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={1.5}
                      justifyContent="space-between"
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                          <Typography variant="subtitle2" fontWeight={700}>
                            {validator.name}
                          </Typography>
                          <Chip size="small" label={validator.hook.replace(/_/g, ' ')} />
                          <Chip
                            size="small"
                            color={validator.on_fail === 'allow' ? 'default' : 'warning'}
                            variant="outlined"
                            label={validator.on_fail.replace(/_/g, ' ')}
                          />
                          <Chip size="small" variant="outlined" label={validator.validation_mode} />
                        </Stack>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.5, wordBreak: 'break-word' }}
                        >
                          {validator.description || validator.prompt || 'No description'}
                        </Typography>
                        {validator.target_names.length > 0 ? (
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                            {validator.target_names.map((target) => (
                              <Chip key={target} size="small" variant="outlined" label={target} />
                            ))}
                          </Stack>
                        ) : null}
                      </Box>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Tooltip title="Validator">
                          <IconButton size="small" onClick={() => openEditValidator(validator)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<LinkIcon />}
                          onClick={() => openNewAssignment(validator.id)}
                        >
                          Assign
                        </Button>
                      </Stack>
                    </Stack>
                  </Box>
                ))
              )}
            </Stack>
          </Stack>
        )}
      </SectionCard>

      <Dialog open={validatorOpen} onClose={closeValidatorDialog} fullWidth maxWidth="md">
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              {editingValidator ? 'Edit validator' : 'New validator'}
            </Typography>
            <IconButton onClick={closeValidatorDialog} aria-label="Close">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Name"
                value={validatorForm.name}
                onChange={(e) =>
                  setValidatorForm((prev) => ({ ...prev, name: e.target.value }))
                }
                helperText="Short name shown on the agent's validator list."
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>Validation method</InputLabel>
                <Select
                  label="Validation method"
                  value={currentMode}
                  onChange={(e) =>
                    setValidatorForm((prev) => ({
                      ...prev,
                      validation_mode: e.target.value as AgentValidatorMode,
                    }))
                  }
                >
                  {MODE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Stack spacing={0.25}>
                        <Typography variant="body2">{option.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.description}
                        </Typography>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{currentModeDescription}</FormHelperText>
              </FormControl>
            </Stack>

            <TextField
              label="Description"
              value={validatorForm.description || ''}
              onChange={(e) =>
                setValidatorForm((prev) => ({ ...prev, description: e.target.value }))
              }
              helperText="Optional internal note for why this validator exists."
              fullWidth
            />

            {usesAgentJudge ? (
              <AgentAutocomplete
                value={validatorForm.validator_agent_id ?? null}
                onChange={(agentId) =>
                  setValidatorForm((prev) => ({
                    ...prev,
                    validator_agent_id: agentId,
                  }))
                }
                label="Validator agent"
                helperText="This agent is called as the judge for this validator."
                required
                filters={{ is_active: true }}
              />
            ) : null}

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Run validator</InputLabel>
                <Select
                  label="Run validator"
                  value={validatorForm.hook}
                  onChange={(e) =>
                    setValidatorForm((prev) => ({
                      ...prev,
                      hook: e.target.value as AgentValidatorHook,
                    }))
                  }
                >
                  {HOOK_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{currentHookDescription}</FormHelperText>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Audit priority</InputLabel>
                <Select
                  label="Audit priority"
                  value={validatorForm.severity || 'medium'}
                  onChange={(e) =>
                    setValidatorForm((prev) => ({
                      ...prev,
                      severity: e.target.value as AgentValidatorSeverity,
                    }))
                  }
                >
                  {SEVERITY_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {currentSeverityDescription} This is stored on validation logs; enforcement comes from the fail action.
                </FormHelperText>
              </FormControl>
            </Stack>

            <TextField
              label={usesAgentJudge ? 'Validator prompt' : 'Notes'}
              value={validatorForm.prompt || ''}
              onChange={(e) =>
                setValidatorForm((prev) => ({ ...prev, prompt: e.target.value }))
              }
              multiline
              minRows={4}
              helperText={
                usesAgentJudge
                  ? 'Instruction sent to the validator agent. Final-response hooks include tool_trace with tool names, inputs, outputs, success/error, and step numbers.'
                  : 'Optional note. Rule checks use the selected checks and config JSON below.'
              }
              fullWidth
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Limit to target</InputLabel>
                <Select
                  label="Limit to target"
                  value={validatorForm.target_type || ''}
                  onChange={(e) =>
                    setValidatorForm((prev) => ({
                      ...prev,
                      target_type: e.target.value,
                    }))
                  }
                >
                  {TARGET_TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value || 'any-target'} value={option.value}>
                      <Stack spacing={0.25}>
                        <Typography variant="body2">{option.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.description}
                        </Typography>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{currentTargetTypeDescription}</FormHelperText>
              </FormControl>
              <TextField
                label="Target names"
                value={validatorTargets}
                onChange={(e) => setValidatorTargets(e.target.value)}
                helperText="Comma-separated. For tool hooks, use tool names. Leave empty for all targets."
                fullWidth
              />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel>If validation passes</InputLabel>
                <Select
                  label="If validation passes"
                  value={validatorForm.on_pass || 'allow'}
                  onChange={(e) =>
                    setValidatorForm((prev) => ({
                      ...prev,
                      on_pass: e.target.value as AgentValidatorAction,
                    }))
                  }
                >
                  {ACTION_OPTIONS.filter((option) => option.value).map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Stack spacing={0.25}>
                        <Typography variant="body2">{option.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.description}
                        </Typography>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {optionDescription(ACTION_OPTIONS, validatorForm.on_pass || 'allow')}
                </FormHelperText>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>If validation fails</InputLabel>
                <Select
                  label="If validation fails"
                  value={validatorForm.on_fail || 'block'}
                  onChange={(e) =>
                    setValidatorForm((prev) => ({
                      ...prev,
                      on_fail: e.target.value as AgentValidatorAction,
                    }))
                  }
                >
                  {ACTION_OPTIONS.filter((option) => option.value).map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Stack spacing={0.25}>
                        <Typography variant="body2">{option.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.description}
                        </Typography>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {optionDescription(ACTION_OPTIONS, validatorForm.on_fail || 'block')}
                </FormHelperText>
              </FormControl>
              <TextField
                label="Retry limit"
                type="number"
                value={validatorForm.max_retries ?? 0}
                onChange={(e) =>
                  setValidatorForm((prev) => ({
                    ...prev,
                    max_retries: Number(e.target.value),
                  }))
                }
                helperText="Used only by retry actions."
              />
            </Stack>

            {usesRuleChecks ? (
              <FormControl fullWidth>
                <InputLabel>Rule checks</InputLabel>
                <Select
                  label="Rule checks"
                  multiple
                  value={csvToList(validatorChecks)}
                  onChange={(e) => {
                    const value = e.target.value;
                    setValidatorChecks(
                      Array.isArray(value) ? value.join(', ') : String(value)
                    );
                  }}
                  renderValue={(selected) =>
                    (selected as string[])
                      .map((value) => optionLabel(CHECK_OPTIONS, value))
                      .join(', ')
                  }
                >
                  {CHECK_OPTIONS.map((check) => (
                    <MenuItem key={check.value} value={check.value}>
                      <Stack spacing={0.25}>
                        <Typography variant="body2">{check.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {check.description}
                        </Typography>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Checks run before any validator-agent judgment. Some checks read values from Config JSON.
                </FormHelperText>
              </FormControl>
            ) : null}

            <TextField
              label="Config JSON"
              value={validatorConfig}
              onChange={(e) => setValidatorConfig(e.target.value)}
              multiline
              minRows={5}
              fullWidth
              helperText="Options used by rule checks and validator actions, such as allowed_tools, blocked_terms, required_args, or max_chars."
              InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.82rem' } }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={validatorForm.is_active !== false}
                  onChange={(e) =>
                    setValidatorForm((prev) => ({
                      ...prev,
                      is_active: e.target.checked,
                    }))
                  }
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeValidatorDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={saveValidator}
            disabled={createValidatorMutation.isPending || updateValidatorMutation.isPending}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={assignmentOpen} onClose={closeAssignmentDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              {editingAssignment ? 'Edit assignment' : 'Assign validator'}
            </Typography>
            <IconButton onClick={closeAssignmentDialog} aria-label="Close">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="info">
              Assignment settings apply only to this agent. Leave override fields blank to use the validator defaults.
            </Alert>

            <FormControl fullWidth disabled={Boolean(editingAssignment)}>
              <InputLabel>Validator</InputLabel>
              <Select
                label="Validator"
                value={assignmentForm.validator || ''}
                onChange={(e) =>
                  setAssignmentForm((prev) => ({
                    ...prev,
                    validator: Number(e.target.value),
                  }))
                }
              >
                {assignmentValidatorOptions.map((validator) => (
                  <MenuItem key={validator.id} value={validator.id}>
                    {validator.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>Select the reusable validator configuration to attach to this agent.</FormHelperText>
            </FormControl>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Order"
                type="number"
                value={assignmentForm.order ?? 100}
                onChange={(e) =>
                  setAssignmentForm((prev) => ({
                    ...prev,
                    order: Number(e.target.value),
                  }))
                }
                fullWidth
                helperText="Lower numbers run earlier when multiple validators match the same hook."
              />
              <FormControl fullWidth>
                <InputLabel>Run-at override</InputLabel>
                <Select
                  label="Run-at override"
                  value={assignmentForm.hook_override || ''}
                  onChange={(e) =>
                    setAssignmentForm((prev) => ({
                      ...prev,
                      hook_override: e.target.value as AgentValidatorHook | '',
                    }))
                  }
                >
                  <MenuItem value="">Use validator default</MenuItem>
                  {HOOK_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>Override when this validator runs for this agent only.</FormHelperText>
              </FormControl>
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Target type override"
                value={assignmentForm.target_type_override || ''}
                onChange={(e) =>
                  setAssignmentForm((prev) => ({
                    ...prev,
                    target_type_override: e.target.value,
                  }))
                }
                fullWidth
                helperText="Use tool, model, or final_response. Leave blank for the validator default."
              />
              <TextField
                label="Target names override"
                value={assignmentTargets}
                onChange={(e) => setAssignmentTargets(e.target.value)}
                fullWidth
                helperText="Comma-separated names. Leave blank for all targets or the validator default."
              />
            </Stack>

            <TextField
              label="Prompt override"
              value={assignmentForm.prompt_override || ''}
              onChange={(e) =>
                setAssignmentForm((prev) => ({
                  ...prev,
                  prompt_override: e.target.value,
                }))
              }
              multiline
              minRows={3}
              fullWidth
              helperText="Optional prompt replacement used only by this agent assignment."
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel>Pass action override</InputLabel>
                <Select
                  label="Pass action override"
                  value={assignmentForm.on_pass_override || ''}
                  onChange={(e) =>
                    setAssignmentForm((prev) => ({
                      ...prev,
                      on_pass_override: e.target.value as AgentValidatorAction | '',
                    }))
                  }
                >
                  {ACTION_OPTIONS.map((option) => (
                    <MenuItem key={option.value || 'default-pass'} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>Leave as default unless this agent needs different pass behavior.</FormHelperText>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Fail action override</InputLabel>
                <Select
                  label="Fail action override"
                  value={assignmentForm.on_fail_override || ''}
                  onChange={(e) =>
                    setAssignmentForm((prev) => ({
                      ...prev,
                      on_fail_override: e.target.value as AgentValidatorAction | '',
                    }))
                  }
                >
                  {ACTION_OPTIONS.map((option) => (
                    <MenuItem key={option.value || 'default-fail'} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>This is the enforcement behavior for failures on this assignment.</FormHelperText>
              </FormControl>
            </Stack>

            <TextField
              label="Override config JSON"
              value={assignmentConfig}
              onChange={(e) => setAssignmentConfig(e.target.value)}
              multiline
              minRows={4}
              fullWidth
              helperText="Merged over the validator's Config JSON for this assignment only."
              InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.82rem' } }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={assignmentForm.is_enabled !== false}
                  onChange={(e) =>
                    setAssignmentForm((prev) => ({
                      ...prev,
                      is_enabled: e.target.checked,
                    }))
                  }
                />
              }
              label="Enabled"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAssignmentDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={saveAssignment}
            disabled={assignMutation.isPending || updateAssignmentMutation.isPending}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default ValidatorsTab;
